<script setup>
// M26.5 / M26.5.1 — read-only AI Profile Drawer. Renders almost entirely
// from data the page already has in memory; the only extra request is one
// on-demand Nexceo::Ai::RuntimeInspector snapshot (existing
// nexceo/leads/:id/ai_runtime endpoint, already used by RuntimeInspectorPage
// and LeadDetailsDrawer) fetched *only when opened* for a project unit that
// has a real recent lead — not a per-card cost, not polling.
//
// M26.5.1 Part 2 — reorganized into an employee-profile shape: Identity,
// Current Assignment, Capabilities, Knowledge, Memory, Recent Decisions,
// Recent Tasks, Timeline (Recent Activities), Ownership Journey, Metrics.
// "Current Conversations"/"Current Customers" from the spec collapse into
// one Current Assignment section — in this domain a lead already *is* the
// conversation and the customer, so three lists of the same three names
// would just be repetition, not more information. Every section stays
// hidden when it has no real data, per the standing project rule.
import { ref, computed, watch } from 'vue';
import aiRuntimeAPI from 'dashboard/api/nexceo/aiRuntime';
import GeneratedAvatar from 'dashboard/components-next/avatar/GeneratedAvatar.vue';
import CapabilityGroups from './CapabilityGroups.vue';
import LiveStatusBadge from './LiveStatusBadge.vue';
import { departmentColor } from './departmentColors';
import { fmt, fmtMs, relativeTime } from './workforceFormat';

const props = defineProps({
  unit: { type: Object, default: null },
  isOpen: { type: Boolean, default: false },
  events: { type: Array, default: () => [] },
});
const emit = defineEmits(['close']);

const color = computed(() => departmentColor(props.unit?.department));

const runtime = ref(null);
const runtimeLoading = ref(false);
const runtimeError = ref(false);

async function loadRuntime() {
  runtime.value = null;
  runtimeError.value = false;
  if (!props.unit?.latestLeadId) return;
  runtimeLoading.value = true;
  try {
    const { data } = await aiRuntimeAPI.show(props.unit.latestLeadId);
    runtime.value = data?.error ? null : data;
  } catch {
    runtimeError.value = true;
  } finally {
    runtimeLoading.value = false;
  }
}

watch(
  () => [props.isOpen, props.unit?.key],
  ([open]) => {
    if (open) loadRuntime();
  }
);

// Timeline / Recent Activities — reuses the page's already-fetched
// account-wide events list, filtered to this unit's project client-side. No
// new backend call (M26.5.1 Part 11: no duplicated calculations).
const projectEvents = computed(() => {
  if (!props.unit?.projectName) return [];
  return props.events
    .filter(e => e.project_name === props.unit.projectName)
    .slice(0, 8);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <Teleport to="body">
    <Transition name="ai-drawer">
      <div
        v-if="isOpen && unit"
        class="fixed inset-0 z-[9998] flex justify-end"
      >
        <div
          class="absolute inset-0 bg-n-slate-12/30 dark:bg-n-slate-1/50"
          @click="emit('close')"
        />
        <div
          class="relative z-10 flex flex-col w-full max-w-lg h-full bg-white dark:bg-n-solid-2 shadow-xl overflow-hidden"
        >
          <!-- Header -->
          <div
            class="flex items-start gap-3 px-5 py-4 border-b border-n-weak dark:border-n-strong shrink-0"
          >
            <GeneratedAvatar
              :name="unit.name"
              :color="color.hex"
              :variant="unit.department"
              :seed="unit.key"
              :size="48"
            />
            <div class="min-w-0 flex-1">
              <h2 class="font-semibold text-base text-n-slate-12 truncate">
                {{ unit.name }}
              </h2>
              <p v-if="unit.poweredBy" class="text-xs text-n-slate-8 truncate">
                Powered by {{ unit.poweredBy }}
              </p>
              <p class="text-xs text-n-slate-9 mt-0.5 truncate">
                {{ unit.roleLabel }}
                <span v-if="unit.projectName"> · {{ unit.projectName }}</span>
              </p>
            </div>
            <button
              class="flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-8 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors ml-3 shrink-0"
              @click="emit('close')"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Scrollable body -->
          <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
            <!-- Identity -->
            <section
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Identity
              </h3>
              <dl class="grid grid-cols-2 gap-y-2.5 gap-x-3 text-sm">
                <dt class="text-n-slate-8">Department</dt>
                <dd class="text-n-slate-12 font-medium">{{ color.label }}</dd>
                <dt class="text-n-slate-8">Status</dt>
                <dd class="text-n-slate-12">
                  <LiveStatusBadge :live-status="unit.liveStatus" />
                </dd>
                <dt class="text-n-slate-8">Health</dt>
                <dd class="text-n-slate-12 font-medium capitalize">
                  {{ unit.health }}
                </dd>
              </dl>
            </section>

            <!-- Current Assignment (covers Current Conversations / Current
                 Customers too — a lead already is both, in this domain) -->
            <section
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Current Assignment
              </h3>
              <ul
                v-if="unit.currentlyWorkingOn?.length"
                class="flex flex-col gap-2"
              >
                <li
                  v-for="lead in unit.currentlyWorkingOn"
                  :key="lead.lead_id"
                  class="flex items-center justify-between gap-2 text-sm"
                >
                  <span class="text-n-slate-12 font-medium truncate">{{
                    lead.name
                  }}</span>
                  <span class="text-n-slate-8 text-xs truncate">{{
                    lead.status_label
                  }}</span>
                </li>
              </ul>
              <p v-else class="text-xs text-n-slate-7">
                No active conversations.
              </p>
            </section>

            <!-- Capabilities -->
            <section
              v-if="unit.capabilities?.length"
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Capabilities
              </h3>
              <CapabilityGroups :capabilities="unit.capabilities" />
            </section>

            <!-- Knowledge / Memory -->
            <section
              v-if="
                unit.knowledgePercent !== undefined ||
                unit.memoryPercent !== undefined
              "
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Knowledge &amp; Memory
              </h3>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <p class="text-lg font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.knowledgePercent, '%') }}
                  </p>
                  <p class="text-[11px] text-n-slate-7">Knowledge Coverage</p>
                </div>
                <div>
                  <p class="text-lg font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.memoryPercent, '%') }}
                  </p>
                  <p class="text-[11px] text-n-slate-7">Profile / Memory</p>
                </div>
              </div>
            </section>

            <!-- Recent Decisions (project units — RuntimeInspector) -->
            <section
              v-if="unit.kind === 'project' && runtime?.decision_stage"
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Recent Decisions
              </h3>
              <p class="text-sm text-n-slate-12 capitalize">
                {{ runtime.decision_stage.replace(/_/g, ' ') }}
              </p>
              <ul
                v-if="runtime.decision_timeline?.length"
                class="flex flex-col gap-1 mt-2"
              >
                <li
                  v-for="(entry, i) in runtime.decision_timeline.slice(0, 4)"
                  :key="i"
                  class="text-xs text-n-slate-11"
                >
                  {{ entry.stage || entry.label || JSON.stringify(entry) }}
                </li>
              </ul>
            </section>

            <!-- Recent Tasks (project units — RuntimeInspector) -->
            <section
              v-if="unit.kind === 'project' && runtime?.pending_tasks?.length"
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Recent Tasks
              </h3>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="(task, i) in runtime.pending_tasks"
                  :key="i"
                  class="text-xs text-n-slate-11 flex items-center gap-1.5"
                >
                  <span
                    class="i-lucide-circle-dot size-2.5 text-woot-500 shrink-0"
                  />
                  {{ task.task_type?.replace(/_/g, ' ') }}
                </li>
              </ul>
            </section>

            <!-- Timeline / Recent Activities — reuses the page's already
                 loaded account-wide events, filtered to this project -->
            <section
              v-if="projectEvents.length"
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3 flex items-center gap-1.5"
              >
                <span class="i-lucide-history size-3.5" />
                Timeline
              </h3>
              <ul class="flex flex-col gap-1.5">
                <li
                  v-for="event in projectEvents"
                  :key="event.id"
                  class="text-xs text-n-slate-11 flex items-center justify-between gap-2"
                >
                  <span class="truncate">{{ event.label }}</span>
                  <span class="text-n-slate-6 shrink-0">{{
                    relativeTime(event.at)
                  }}</span>
                </li>
              </ul>
            </section>

            <!-- Ownership Journey (project units — RuntimeInspector) -->
            <section
              v-if="
                unit.kind === 'project' && runtime?.ownership_history?.length
              "
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Ownership Journey
              </h3>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="(entry, i) in runtime.ownership_history.slice(0, 5)"
                  :key="i"
                  class="text-xs text-n-slate-11"
                >
                  {{ entry.to || entry.owner || JSON.stringify(entry) }}
                </li>
              </ul>
            </section>

            <!-- Metrics -->
            <section
              class="rounded-xl border border-n-weak dark:border-n-strong p-4"
            >
              <h3
                class="text-xs font-semibold text-n-slate-7 uppercase tracking-wider mb-3"
              >
                Metrics
              </h3>
              <div class="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p class="text-sm font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.leadsManaged) }}
                  </p>
                  <p class="text-[10px] text-n-slate-7">Leads</p>
                </div>
                <div>
                  <p class="text-sm font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.aiReplies) }}
                  </p>
                  <p class="text-[10px] text-n-slate-7">Replies</p>
                </div>
                <div>
                  <p class="text-sm font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.documentsShared) }}
                  </p>
                  <p class="text-[10px] text-n-slate-7">Documents</p>
                </div>
                <div>
                  <p class="text-sm font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.siteVisits) }}
                  </p>
                  <p class="text-[10px] text-n-slate-7">Visits</p>
                </div>
                <div>
                  <p class="text-sm font-bold text-n-slate-12 tabular-nums">
                    {{ fmt(unit.queueCount) }}
                  </p>
                  <p class="text-[10px] text-n-slate-7">Queue</p>
                </div>
                <div>
                  <p class="text-sm font-bold text-n-slate-12 tabular-nums">
                    {{ fmtMs(unit.avgResponseMs) }}
                  </p>
                  <p class="text-[10px] text-n-slate-7">Avg Response</p>
                </div>
              </div>
              <div
                v-if="unit.extraKpis?.length"
                class="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-n-weak dark:border-n-strong"
              >
                <span
                  v-for="kpi in unit.extraKpis"
                  :key="kpi.label"
                  class="text-[11px] px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-8"
                >
                  <b class="text-n-slate-11">{{ kpi.value }}</b> {{ kpi.label }}
                </span>
              </div>
            </section>

            <p
              v-if="
                unit.kind === 'project' &&
                !runtimeLoading &&
                !runtime &&
                !runtimeError
              "
              class="text-xs text-n-slate-7 px-1"
            >
              No recent runtime snapshot available for this project yet.
            </p>
            <div
              v-if="unit.kind === 'project' && runtimeLoading"
              class="flex items-center gap-2 text-xs text-n-slate-7 py-1"
            >
              <span class="i-lucide-loader-2 size-3.5 animate-spin" />
              Loading latest conversation state…
            </div>
            <p
              v-if="unit.kind !== 'project'"
              class="text-xs text-n-slate-7 px-1"
            >
              This role isn't tied to a single lead conversation, so
              conversation-level runtime data isn't shown here — the metrics
              above are account-wide aggregates.
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.ai-drawer-enter-active {
  transition: opacity 0.2s ease;
}
.ai-drawer-leave-active {
  transition: opacity 0.15s ease;
}
.ai-drawer-enter-active .relative {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.ai-drawer-leave-active .relative {
  transition: transform 0.2s ease;
}
.ai-drawer-enter-from,
.ai-drawer-leave-to {
  opacity: 0;
}
.ai-drawer-enter-from .relative {
  transform: translateX(100%);
}
.ai-drawer-leave-to .relative {
  transform: translateX(100%);
}
</style>
